import PasswordInput from '../components/PasswordInput';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';
import { getDepartments, getPositions } from '../services/organizationService';
import type { User, CreateUserData } from '../types/user';
import type { Department, Position } from '../types/organization';
import Pagination from '../components/Pagination';

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [employment, setEmployment] = useState<Employment>('active');
  const [createFormData, setCreateFormData] = useState<CreateUserData>({
    username: '',
    password: '',
    name: '',
    email: '',
    employeeNumber: '',
    role: 'employee',
    departmentId: null,
    positionId: null,
    hireDate: null,
    employmentType: 'full_time',
    workScheduleType: 'shift',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<{
    name: string;
    email: string;
    employeeNumber: string;
    departmentId: number | null;
    positionId: number | null;
    hireDate: string | null;
    employmentType: 'full_time' | 'part_time' | 'other';
    workScheduleType: 'fixed' | 'shift';
  }>({
    name: '',
    email: '',
    employeeNumber: '',
    departmentId: null,
    positionId: null,
    hireDate: null,
    employmentType: 'full_time',
    workScheduleType: 'shift',
  });
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    userId: number;
    username: string;
    name: string;
    temporaryPassword: string;
  } | null>(null);

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    loadData();
  }, []);

  // 在職状態の絞り込みは**サーバー側で効かせる**（手元で filter すると
  // ページングの件数と実データがずれる）。変更したら1ページ目へ戻す。
  useEffect(() => {
    setCurrentPage(1);
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employment]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [usersData, deptsData, posData] = await Promise.all([
        userService.getUsers(employment),
        getDepartments(),
        getPositions()
      ]);
      setUsers(usersData);
      setDepartments(deptsData);
      setPositions(posData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await userService.getUsers(employment);
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'ユーザー一覧の取得に失敗しました');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');

    try {
      await userService.createUser(createFormData);
      setShowCreateForm(false);
      setCreateFormData({
        username: '',
        password: '',
        name: '',
        email: '',
        employeeNumber: '',
        role: 'employee',
        departmentId: null,
        positionId: null,
        hireDate: null,
      });
      await loadUsers();
      setSuccessMessage('ユーザーが作成されました');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'ユーザー作成に失敗しました');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * 退職 / 復職。**辞めた人を外すときの通常操作。**
   *
   * 記録（打刻・有給・申請）はそのまま残り、ログインができなくなり、
   * 翌月からの請求人数から外れる。取り消せるので確認は軽くてよい。
   */
  const handleSetRetired = async (user: User, retired: boolean) => {
    const label = retired ? '退職' : '復職';
    const message = retired
      ? [
          `「${user.name}（${user.employee_number}）」を退職にします。`,
          '',
          '・ログインできなくなります',
          '・翌月からの請求人数に含まれなくなります',
          '・打刻記録や有給の残数はそのまま残ります',
          '',
          'あとから復職に戻せます。よろしいですか？',
        ].join('\n')
      : `「${user.name}（${user.employee_number}）」を復職させます。ログインと打刻ができるようになります。`;

    if (!window.confirm(message)) return;

    try {
      setError('');
      setSuccessMessage('');
      await userService.setRetired(user.id, retired);
      setSuccessMessage(`「${user.name}」を${label}にしました`);
      await loadUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || `${label}の処理に失敗しました`);
    }
  };

  /**
   * 従業員を削除する。
   *
   * ⚠️ **削除は物理削除で、その従業員の打刻記録も一緒に消える。**
   * 労働時間の記録には保存義務があるため、消える件数と期間を必ず先に見せる。
   * 「退職者だから名簿から消す」つもりの操作で、法定の記録が復元不能に
   * 失われることを防ぐのがこの確認の目的。
   */
  const handleDeleteUser = async (userId: number, username: string) => {
    let impact;
    try {
      setError('');
      impact = await userService.getDeletionImpact(userId);
    } catch (err: any) {
      // 件数が取れないまま消させない。分からない状態での削除が一番危ない。
      setError(err.response?.data?.error || '削除の影響を確認できなかったため、削除を中止しました');
      return;
    }

    const records: string[] = [];
    if (impact.attendance.count > 0) {
      const period =
        impact.attendance.from && impact.attendance.to
          ? `（${impact.attendance.from} 〜 ${impact.attendance.to}）`
          : '';
      records.push(`・打刻記録 ${impact.attendance.count} 件${period}`);
    }
    if (impact.leaveBalances > 0) records.push(`・有給休暇の付与・残数 ${impact.leaveBalances} 件`);
    if (impact.leaveRequests > 0) records.push(`・休暇申請 ${impact.leaveRequests} 件`);
    if (impact.correctionRequests > 0) records.push(`・打刻の修正申請 ${impact.correctionRequests} 件`);

    const message =
      records.length > 0
        ? [
            `ユーザー「${username}」を完全に削除します。`,
            '',
            '次の記録も一緒に削除され、元に戻せません。',
            ...records,
            '',
            '労働時間の記録には保存義務があります。',
            '必要な記録は、削除する前に勤怠エクスポートで保存してください。',
            '',
            '記録を残したまま在籍から外すだけなら「退職」を使ってください。',
            '',
            '削除してよろしいですか？',
          ].join('\n')
        : `ユーザー「${username}」を削除しますか？\n\nこのユーザーの記録はまだありません。`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      await userService.deleteUser(userId);
      setSuccessMessage(`ユーザー「${username}」を削除しました`);
      await loadUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'ユーザー削除に失敗しました');
    }
  };

  const handleStartEdit = (user: User) => {
    setEditingUserId(user.id);
    setEditFormData({
      name: user.name,
      email: user.email || '',
      employeeNumber: user.employee_number,
      departmentId: user.department_id || null,
      positionId: user.position_id || null,
      hireDate: user.hire_date || null,
      employmentType: user.employment_type || 'full_time',
      workScheduleType: user.work_schedule_type || 'shift',
    });
    setError('');
    setSuccessMessage('');
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditFormData({
      name: '',
      email: '',
      employeeNumber: '',
      departmentId: null,
      positionId: null,
      hireDate: null,
      employmentType: 'full_time',
      workScheduleType: 'shift',
    });
  };

  const handleUpdateUser = async (userId: number) => {
    try {
      setError('');
      setSuccessMessage('');
      await userService.updateUser(userId, editFormData);
      setSuccessMessage('ユーザー情報を更新しました');
      setEditingUserId(null);
      await loadUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'ユーザー更新に失敗しました');
    }
  };

  const handleResetPassword = async (userId: number) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;

    if (!window.confirm(`ユーザー「${targetUser.username}」のパスワードをリセットしますか？\n一時パスワードが発行されます。`)) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      const result = await userService.resetPassword(userId);
      setResetPasswordResult({
        userId,
        username: result.username,
        name: result.name,
        temporaryPassword: result.temporaryPassword
      });
      setSuccessMessage('パスワードをリセットしました。一時パスワードをユーザーに伝えてください。');
    } catch (err: any) {
      setError(err.response?.data?.error || 'パスワードリセットに失敗しました');
    }
  };

  const handleClosePasswordResetModal = () => {
    setResetPasswordResult(null);
  };

  // 権限チェック
  const canManageUsers = currentUser && ['master', 'admin'].includes(currentUser.role);

  // ページネーション用のデータ計算
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = users.slice(startIndex, endIndex);

  if (!canManageUsers) {
    return (
      <div style={{ maxWidth: '1200px', margin: '50px auto', padding: 'clamp(10px, 3vw, 20px)' }}>
        <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)' }}>アクセス権限がありません</h1>
        <p style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>この画面を表示する権限がありません。</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            whiteSpace: 'nowrap'
          }}
        >
          ダッシュボードに戻る
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '50px auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'clamp(15px, 4vw, 20px)', flexWrap: 'wrap', gap: '10px' }}>
        <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>ユーザー管理</h1>
        <button onClick={() => navigate('/dashboard')} style={{ padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)', fontSize: 'clamp(12px, 2.5vw, 14px)', whiteSpace: 'nowrap', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          ダッシュボードに戻る
        </button>
      </div>

      {error && (
        <div style={{
          color: '#721c24',
          backgroundColor: '#f8d7da',
          marginBottom: 'clamp(15px, 4vw, 20px)',
          padding: 'clamp(8px, 2vw, 10px)',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          fontSize: 'clamp(12px, 2.5vw, 14px)'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          color: '#155724',
          backgroundColor: '#d4edda',
          marginBottom: 'clamp(15px, 4vw, 20px)',
          padding: 'clamp(8px, 2vw, 10px)',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          fontSize: 'clamp(12px, 2.5vw, 14px)'
        }}>
          {successMessage}
        </div>
      )}

      {!showCreateForm && (
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            marginBottom: 'clamp(15px, 4vw, 20px)',
            padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            whiteSpace: 'nowrap'
          }}
        >
          + 新規ユーザー作成
        </button>
      )}

      {showCreateForm && (
        <div style={{ marginBottom: 'clamp(20px, 5vw, 30px)', padding: 'clamp(12px, 3vw, 20px)', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
          <h2 style={{ fontSize: 'clamp(16px, 4vw, 20px)', marginTop: 0, marginBottom: 'clamp(12px, 3vw, 16px)' }}>新規ユーザー作成</h2>
          <form onSubmit={handleCreateUser}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'clamp(12px, 3vw, 15px)' }}>
              <div>
                <label htmlFor="employeeNumber" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>社員番号</label>
                <input
                  type="text"
                  id="employeeNumber"
                  value={createFormData.employeeNumber || ''}
                  onChange={(e) => setCreateFormData({ ...createFormData, employeeNumber: e.target.value })}
                  maxLength={32}
                  placeholder="空欄なら自動で採番"
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
                <small style={{ fontSize: 'clamp(10px, 2vw, 11px)', color: '#6c757d' }}>
                  同姓同名を区別するための番号です。帳簿やCSVに出力されます
                </small>
              </div>

              <div>
                <label htmlFor="username" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>ログインID *</label>
                <input
                  type="text"
                  id="username"
                  value={createFormData.username}
                  onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
                  required
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="password" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>パスワード *</label>
                <PasswordInput
                  id="password"
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                  required
                  minLength={6}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="name" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>名前 *</label>
                <input
                  type="text"
                  id="name"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="email" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>メールアドレス</label>
                <input
                  type="email"
                  id="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="department" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>部署</label>
                <select
                  id="department"
                  value={createFormData.departmentId || ''}
                  onChange={(e) => setCreateFormData({ ...createFormData, departmentId: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                >
                  <option value="">未所属</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="position" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>役職</label>
                <select
                  id="position"
                  value={createFormData.positionId || ''}
                  onChange={(e) => setCreateFormData({ ...createFormData, positionId: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                >
                  <option value="">未設定</option>
                  {positions.map(pos => (
                    <option key={pos.id} value={pos.id}>{pos.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="hireDate" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>入社年月日</label>
                <input
                  type="date"
                  id="hireDate"
                  value={createFormData.hireDate || ''}
                  onChange={(e) => setCreateFormData({ ...createFormData, hireDate: e.target.value || null })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                />
              </div>

              {/*
                雇用区分と勤務形態。**役職(権限)とは別の軸**。
                勤務形態は希望シフトの受付の既定の対象を決める
              */}
              <div>
                <label htmlFor="employmentType" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>雇用区分</label>
                <select
                  id="employmentType"
                  value={createFormData.employmentType || 'full_time'}
                  onChange={(e) => setCreateFormData({ ...createFormData, employmentType: e.target.value as any })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                >
                  <option value="full_time">正社員</option>
                  <option value="part_time">アルバイト・パート</option>
                  <option value="other">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="workScheduleType" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>勤務形態</label>
                <select
                  id="workScheduleType"
                  value={createFormData.workScheduleType || 'shift'}
                  onChange={(e) => setCreateFormData({ ...createFormData, workScheduleType: e.target.value as any })}
                  style={{ width: '100%', padding: 'clamp(6px, 1.5vw, 8px)', marginTop: '5px', fontSize: 'clamp(12px, 2.5vw, 14px)', border: '1px solid #dee2e6', borderRadius: '4px' }}
                >
                  <option value="shift">シフト制（毎回の提出で決まる）</option>
                  <option value="fixed">固定制（曜日と時間が決まっている）</option>
                </select>
                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                  シフト制の人が、希望シフトの受付の既定の対象になります
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'clamp(8px, 2vw, 10px)', marginTop: 'clamp(15px, 4vw, 20px)', flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={isCreating}
                style={{
                  padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  whiteSpace: 'nowrap',
                  opacity: isCreating ? 0.6 : 1
                }}
              >
                {isCreating ? '作成中...' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  whiteSpace: 'nowrap'
                }}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'clamp(8px, 2vw, 12px)' }}>
        <EmploymentFilter value={employment} onChange={setEmployment} count={users.length} />
      </div>

      {isLoading ? (
        <p>読み込み中...</p>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
        <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', backgroundColor: 'white' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>社員番号</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>ログインID</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>名前</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>メールアドレス</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>部署</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>役職</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>入社年月日</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>作成日</th>
              <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => {
              const isEditing = editingUserId === user.id;
              const canEdit = currentUser?.role === 'master' || (currentUser?.role === 'admin' && user.role !== 'master');

              return (
                <React.Fragment key={user.id}>
                <tr style={{ borderBottom: '1px solid #dee2e6', backgroundColor: isEditing ? '#fff3cd' : user.retired_at ? '#f8f9fa' : 'transparent', color: user.retired_at ? '#6c757d' : 'inherit' }}>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.employeeNumber}
                        onChange={(e) => setEditFormData({ ...editFormData, employeeNumber: e.target.value })}
                        maxLength={32}
                        style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                      />
                    ) : (
                      user.employee_number
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {user.username}
                    {user.retired_at && (
                      <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', background: '#e9ecef', color: '#6c757d', fontSize: 'clamp(10px, 2vw, 11px)', whiteSpace: 'nowrap' }}>
                        退職
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                      />
                    ) : (
                      user.name
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      user.role === 'master' ? (
                        <div style={{ padding: '4px', color: '#6c757d', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                          {user.email || '-'}<br />
                          <small style={{ fontSize: 'clamp(10px, 2vw, 12px)' }}>(専用の変更機能をご利用ください)</small>
                        </div>
                      ) : (
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                        />
                      )
                    ) : (
                      user.email || '-'
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      <select
                        value={editFormData.departmentId || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, departmentId: e.target.value ? parseInt(e.target.value) : null })}
                        style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                      >
                        <option value="">未所属</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    ) : (
                      user.department_name || '-'
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      user.role === 'master' ? (
                        <div style={{ padding: '4px', color: '#6c757d', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                          {user.position_name || '-'} (変更不可)
                        </div>
                      ) : (
                        <select
                          value={editFormData.positionId || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, positionId: e.target.value ? parseInt(e.target.value) : null })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                        >
                          <option value="">未設定</option>
                          {positions.map(pos => (
                            <option key={pos.id} value={pos.id}>{pos.name}</option>
                          ))}
                        </select>
                      )
                    ) : (
                      user.position_name || '-'
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {isEditing ? (
                      <div style={{ display: 'grid', gap: '4px' }}>
                        <input
                          type="date"
                          value={editFormData.hireDate || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, hireDate: e.target.value || null })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: 'clamp(12px, 2.5vw, 14px)' }}
                        />
                        {/* 列を増やさずに済ませる。表の見出しを触ると他の画面まで影響が出る */}
                        <select
                          value={editFormData.employmentType}
                          onChange={(e) => setEditFormData({ ...editFormData, employmentType: e.target.value as any })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '12px' }}
                        >
                          <option value="full_time">正社員</option>
                          <option value="part_time">アルバイト・パート</option>
                          <option value="other">その他</option>
                        </select>
                        <select
                          value={editFormData.workScheduleType}
                          onChange={(e) => setEditFormData({ ...editFormData, workScheduleType: e.target.value as any })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '12px' }}
                        >
                          <option value="shift">シフト制</option>
                          <option value="fixed">固定制</option>
                        </select>
                      </div>
                    ) : (
                      <div>
                        <div>{user.hire_date ? new Date(user.hire_date).toLocaleDateString('ja-JP') : '-'}</div>
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '10px', background: '#F1F5F9', color: '#475569', borderRadius: '3px', padding: '1px 5px' }}>
                            {user.employment_type === 'part_time' ? 'アルバイト・パート' : user.employment_type === 'other' ? 'その他' : '正社員'}
                          </span>
                          <span style={{ fontSize: '10px', background: user.work_schedule_type === 'fixed' ? '#F1F5F9' : '#DBEAFE', color: user.work_schedule_type === 'fixed' ? '#475569' : '#1D4ED8', borderRadius: '3px', padding: '1px 5px' }}>
                            {user.work_schedule_type === 'fixed' ? '固定制' : 'シフト制'}
                          </span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {new Date(user.created_at).toLocaleDateString('ja-JP')}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 'clamp(4px, 1vw, 5px)', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleUpdateUser(user.id)}
                          style={{
                            padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: 'clamp(10px, 2vw, 12px)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          保存
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: 'clamp(10px, 2vw, 12px)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          キャンセル
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 'clamp(4px, 1vw, 5px)', flexWrap: 'wrap' }}>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleStartEdit(user)}
                              style={{
                                padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 12px)',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              編集
                            </button>
                            {user.role !== 'master' && (
                              <button
                                onClick={() => handleResetPassword(user.id)}
                                style={{
                                  padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                                  backgroundColor: '#ffc107',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: 'clamp(10px, 2vw, 12px)',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                パスワードリセット
                              </button>
                            )}
                          </>
                        )}
                        {user.id !== currentUser?.id && canEdit && (
                          <>
                            {/* 日常の操作はこちら。記録を残したまま在籍から外す */}
                            <button
                              onClick={() => handleSetRetired(user, !user.retired_at)}
                              style={{
                                padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                                backgroundColor: user.retired_at ? '#198754' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 12px)',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {user.retired_at ? '復職' : '退職'}
                            </button>
                            {/* 記録ごと消える操作。退職者に対してのみ出す */}
                            {user.retired_at && (
                              <button
                                onClick={() => handleDeleteUser(user.id, user.username)}
                                style={{
                                  padding: 'clamp(4px, 1vw, 5px) clamp(8px, 2vw, 10px)',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: 'clamp(10px, 2vw, 12px)',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                完全に削除
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {!isLoading && users.length === 0 && (
        <p style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
          ユーザーが登録されていません
        </p>
      )}

      {/* ページネーション */}
      {!isLoading && users.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={users.length}
        />
      )}

      {/* パスワードリセット結果モーダル */}
      {resetPasswordResult && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleClosePasswordResetModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: 'clamp(16px, 4vw, 30px)',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: 'clamp(12px, 3vw, 20px)', fontSize: 'clamp(16px, 4vw, 20px)' }}>パスワードリセット完了</h2>
            <p style={{ marginBottom: 'clamp(12px, 3vw, 20px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
              ユーザー「{resetPasswordResult.name} ({resetPasswordResult.username})」のパスワードをリセットしました。
            </p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: 'clamp(10px, 2.5vw, 15px)',
              borderRadius: '4px',
              marginBottom: 'clamp(12px, 3vw, 20px)',
              border: '1px solid #dee2e6'
            }}>
              <strong style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>一時パスワード:</strong>
              <div style={{
                fontSize: 'clamp(18px, 5vw, 24px)',
                fontFamily: 'monospace',
                marginTop: 'clamp(8px, 2vw, 10px)',
                padding: 'clamp(8px, 2vw, 10px)',
                backgroundColor: 'white',
                border: '2px solid #007bff',
                borderRadius: '4px',
                textAlign: 'center',
                userSelect: 'all',
                wordBreak: 'break-all'
              }}>
                {resetPasswordResult.temporaryPassword}
              </div>
            </div>
            <p style={{ fontSize: 'clamp(11px, 2.5vw, 14px)', color: '#6c757d', marginBottom: 'clamp(12px, 3vw, 20px)' }}>
              ⚠️ この一時パスワードをユーザーに直接伝えてください。<br />
              ユーザーは次回ログイン時にパスワード変更が必要になります。
            </p>
            <div style={{ display: 'flex', gap: 'clamp(8px, 2vw, 10px)', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(resetPasswordResult.temporaryPassword);
                  alert('一時パスワードをクリップボードにコピーしました');
                }}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  whiteSpace: 'nowrap'
                }}
              >
                コピー
              </button>
              <button
                onClick={handleClosePasswordResetModal}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  whiteSpace: 'nowrap'
                }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
